import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import core, { App, GQL, Viewport } from '@gin-betting/core';
import matchList from '@gin-betting/match-list';
import { OrderedSet } from 'immutable';
import { createSelector, createStructuredSelector } from 'reselect';

import { ExtendedRouts } from 'app-constants';
import { useMemoSelector, usePrevious } from 'hooks';
import favorite from 'modules/favorite';
import { getByKey, getDefaultVariablesFavorite } from 'utils';
import messages from '../../messages';

const {
    selectors: { selectFavoriteIds },
} = favorite;

const {
    appServiceLocator,
    utils: { checkOnRoute },
    entities: {
        normalizeEntity,
        selectors: { selectMatches },
    },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

const {
    constants: { GET_FAVORITE_MATCHES_SUCCESS },
} = matchList;

const mappingMsgByPathname = {
    [ExtendedRouts.FAVORITE_UPCOMING]: messages.upcomingFavorite,
    [ExtendedRouts.FAVORITE_OUTRIGHTS]: messages.outrightsFavorite,
    [ExtendedRouts.FAVORITE_RESULTS]: messages.resultsFavorite,
};

interface IOutput {
    currFavoriteIds: OrderedSet<string>;
    fetchStatus: App.FetchStatus;
    filteredMatchIds: OrderedSet<string>;
    isOutrights: boolean;
    msg: string | undefined;
    lessThanOrEqual: Viewport.Breakpoints;
    groupedIdsByTournament: ReturnType<typeof selectGroupedIdsByTournament>;
}

export const useFavoriteList = (): IOutput => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {
        lessThanOrEqual,
        matchIds,
        groupedIdsByTournament,
    } = useMemoSelector(selectProps, { location });

    const intl = useIntl();
    const [currFavoriteIds, setCurrFavoriteIds] = useState<OrderedSet<string>>(
        OrderedSet()
    );
    const [fetchStatus, setFetchStatus] = useState<App.FetchStatus>(
        App.FetchStatus.INITIAL
    );

    const requestFavoriteMatches = useCallback(
        (
            { withLoading }: { withLoading: boolean },
            allMatchIds: ReturnType<typeof selectFavoriteIds>
        ) => {
            setCurrFavoriteIds(prevCurrFavoriteIds =>
                prevCurrFavoriteIds.clear()
            );
            const isOutRights = checkOnRoute(ExtendedRouts.FAVORITE_OUTRIGHTS);
            const isUpComing = checkOnRoute(ExtendedRouts.FAVORITE_UPCOMING);

            const variables: GQL.GetMatchesByEventIdsQueryVariables = {
                ...getDefaultVariablesFavorite(),
                sportEventIds: allMatchIds.toArray(),
                isTopMarkets: !isOutRights,
                withMarketsCount: isUpComing,
                marketLimit: isOutRights ? null : 3,
            };

            if (withLoading) {
                setFetchStatus(App.FetchStatus.LOADING);
            }

            appServiceLocator
                .get('apiClient')
                .makeRequest({
                    method: 'getMatchesByEventIds',
                    variables,
                })
                .then(response => {
                    const incomeMatchIds = response.data.matches.map(
                        match => match.id
                    );
                    const normalizedData = normalizeEntity(response.data);
                    dispatch({
                        type: GET_FAVORITE_MATCHES_SUCCESS,
                        payload: {
                            ...normalizedData,
                            listType: '/',
                            isTopMarkets: true,
                        },
                    });
                    setCurrFavoriteIds(allMatchIds.intersect(incomeMatchIds));
                    setFetchStatus(prev =>
                        withLoading ? App.FetchStatus.LOADED : prev
                    );
                })
                .catch(err => {
                    console.error(err);

                    if (withLoading) {
                        setFetchStatus(App.FetchStatus.LOADED);
                    }
                });
        },
        [dispatch]
    );

    const prevMatchIds = usePrevious(matchIds);
    const isAllMatchesExist =
        !prevMatchIds || matchIds.subtract(prevMatchIds).count() === 0;

    const prevPathName = usePrevious(location.pathname);
    const isLocationChanged = prevPathName !== location.pathname;

    useEffect(() => {
        if (isAllMatchesExist && !isLocationChanged) return;
        if (matchIds.count() === 0) {
            setFetchStatus(App.FetchStatus.LOADED);

            return;
        }

        requestFavoriteMatches({ withLoading: isLocationChanged }, matchIds);
    }, [
        requestFavoriteMatches,
        isAllMatchesExist,
        matchIds,
        isLocationChanged,
    ]);

    const filteredMatchIds = currFavoriteIds.intersect(matchIds);
    const isOutrights = checkOnRoute(ExtendedRouts.FAVORITE_OUTRIGHTS);

    const msgDesc = getByKey(mappingMsgByPathname, location.pathname);
    const msg = msgDesc ? intl.formatMessage(msgDesc) : undefined;

    return {
        currFavoriteIds,
        fetchStatus,
        filteredMatchIds,
        isOutrights,
        msg,
        lessThanOrEqual,
        groupedIdsByTournament,
    };
};

const selectGroupedIdsByTournament = createSelector(
    selectFavoriteIds,
    selectMatches,
    (matchIds, matches) => {
        return matchIds.groupBy(matchId => {
            const match = matches.get(matchId);

            return !match || match.tournament;
        });
    }
);

const selectProps = createStructuredSelector({
    lessThanOrEqual: makeViewportSelector('lessThanOrEqual'),
    matchIds: selectFavoriteIds,
    groupedIdsByTournament: selectGroupedIdsByTournament,
});
